import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

const SERVICES = [
  { id: "early_intervention", label: "Early Intervention" },
  { id: "justice_support", label: "Justice Support" },
  { id: "reintegration", label: "Reintegration" },
  { id: "family", label: "Family & Carer Support" },
  { id: "social_work", label: "Social Work Services" },
  { id: "advocacy", label: "Advocacy" },
  { id: "peer", label: "Peer Support" },
];
const JUSTICE = [
  { v: "none", l: "No current justice involvement" },
  { v: "bail", l: "On bail" },
  { v: "community_order", l: "Community-based order" },
  { v: "parole", l: "On parole" },
  { v: "incarceration", l: "Currently incarcerated" },
  { v: "other", l: "Other / unsure" },
];

const initial = {
  referral_type: "self",
  client_first_name: "", client_last_name: "", client_age: "",
  client_phone: "", client_email: "", client_location: "",
  referrer_name: "", referrer_relationship: "", referrer_phone: "", referrer_email: "", referrer_organisation: "",
  service_types: [], justice_involvement: "none", urgency: "standard",
  reason: "", additional_info: "", consent_given: false,
};

const inputCls = "w-full border-0 border-b border-[var(--njas-line-strong)] bg-transparent py-3 text-njas-text focus:outline-none focus:border-njas-sage transition placeholder:text-njas-muted/60";
const selectCls = "w-full border-0 border-b border-[var(--njas-line-strong)] bg-transparent py-3 text-njas-text focus:outline-none focus:border-njas-sage transition";

function Field({ label, children, required, testid, help }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-wrapper` : undefined}>
      <span className="njas-eyebrow text-njas-navy block">{label} {required && <span className="text-njas-sage">*</span>}</span>
      {children}
      {help && <span className="mt-2 block text-xs text-njas-muted">{help}</span>}
    </label>
  );
}

export default function Refer() {
  const [f, set] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const upd = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleService = (id) => set((prev) => ({ ...prev, service_types: prev.service_types.includes(id) ? prev.service_types.filter((s) => s !== id) : [...prev.service_types, id] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_given) return toast.error("Consent is required to submit a referral");
    if (f.service_types.length === 0) return toast.error("Please choose at least one service");
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
      const { data } = await api.post("/public/referrals", payload);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 fade-in" data-testid="refer-success">
        <div className="border border-[var(--njas-sage)] p-10 lg:p-14 text-center bg-[var(--njas-bg-alt)]">
          <div className="njas-badge mx-auto"><CheckCircle2 className="w-6 h-6" /></div>
          <div className="njas-script text-5xl text-njas-sage-deep mt-6">Thank you.</div>
          <h1 className="njas-display text-5xl text-njas-navy mt-2 leading-[0.9]">WE'VE&nbsp;GOT&nbsp;IT.</h1>
          <p className="mt-6 text-njas-text leading-relaxed max-w-md mx-auto">
            Your referral has been received. An NJAS team member will review it and be in touch as soon as possible.
          </p>
          <div className="mt-8 inline-block px-6 py-3 bg-white border border-[var(--njas-sage)] text-njas-navy font-mono text-sm">
            Reference: <strong>{done.reference_code}</strong>
          </div>
          <p className="mt-8 text-xs text-njas-muted">
            If this is a crisis, please call Lifeline <strong>13&nbsp;11&nbsp;14</strong> or <strong>000</strong> in an emergency.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="refer-page" className="fade-in">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
        <div className="njas-eyebrow">Referral Form</div>
        <div className="njas-script text-5xl text-njas-sage-deep mt-6">Confidential &amp; free.</div>
        <h1 className="njas-display text-6xl sm:text-8xl text-njas-navy mt-2 leading-[0.88]">
          REFER&nbsp;YOURSELF,<br/>OR&nbsp;SOMEONE&nbsp;ELSE.
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-njas-muted leading-relaxed">
          If you're supporting someone else, please have their consent where possible. A member of our team will review your referral and follow up.
        </p>
      </section>

      <form onSubmit={submit} className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pb-24 space-y-14" data-testid="referral-form">
        {/* Referral type */}
        <div className="pt-10 border-t border-[var(--njas-sage)]/50">
          <div className="njas-eyebrow mb-6">01 · Who is this for?</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[{ v: "self", l: "Myself" }, { v: "third_party", l: "Someone else / third-party" }].map((opt) => (
              <label key={opt.v} className={`p-6 border-2 cursor-pointer transition ${f.referral_type === opt.v ? "border-njas-navy bg-[var(--njas-bg-alt)]" : "border-[var(--njas-line-strong)] hover:border-njas-sage"}`} data-testid={`refer-type-${opt.v}`}>
                <input type="radio" name="referral_type" value={opt.v} checked={f.referral_type === opt.v} onChange={upd("referral_type")} className="sr-only" />
                <div className="njas-display text-2xl text-njas-navy">{opt.l}</div>
              </label>
            ))}
          </div>
        </div>

        {/* Client */}
        <div className="pt-10 border-t border-[var(--njas-sage)]/50">
          <div className="njas-eyebrow mb-6">02 · Client details</div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
            <Field label="First name" required><input required value={f.client_first_name} onChange={upd("client_first_name")} className={inputCls} data-testid="refer-first-name" /></Field>
            <Field label="Last name" required><input required value={f.client_last_name} onChange={upd("client_last_name")} className={inputCls} data-testid="refer-last-name" /></Field>
            <Field label="Age or age group"><input placeholder="17 / 28-34" value={f.client_age} onChange={upd("client_age")} className={inputCls} data-testid="refer-age" /></Field>
            <Field label="Suburb / location"><input value={f.client_location} onChange={upd("client_location")} className={inputCls} data-testid="refer-location" /></Field>
            <Field label="Phone"><input value={f.client_phone} onChange={upd("client_phone")} className={inputCls} data-testid="refer-phone" /></Field>
            <Field label="Email"><input type="email" value={f.client_email} onChange={upd("client_email")} className={inputCls} data-testid="refer-email" /></Field>
          </div>
        </div>

        {/* Referrer */}
        {f.referral_type === "third_party" && (
          <div className="pt-10 border-t border-[var(--njas-sage)]/50" data-testid="referrer-section">
            <div className="njas-eyebrow mb-6">Your details</div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Your name" required><input required value={f.referrer_name} onChange={upd("referrer_name")} className={inputCls} data-testid="referrer-name" /></Field>
              <Field label="Relationship" help="Parent, carer, case worker, teacher, etc."><input value={f.referrer_relationship} onChange={upd("referrer_relationship")} className={inputCls} data-testid="referrer-rel" /></Field>
              <Field label="Phone"><input value={f.referrer_phone} onChange={upd("referrer_phone")} className={inputCls} data-testid="referrer-phone" /></Field>
              <Field label="Email"><input type="email" value={f.referrer_email} onChange={upd("referrer_email")} className={inputCls} data-testid="referrer-email" /></Field>
              <Field label="Organisation"><input value={f.referrer_organisation} onChange={upd("referrer_organisation")} className={inputCls} data-testid="referrer-org" /></Field>
            </div>
          </div>
        )}

        {/* Services */}
        <div className="pt-10 border-t border-[var(--njas-sage)]/50">
          <div className="njas-eyebrow mb-6">03 · Kind of support needed</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {SERVICES.map((s) => {
              const active = f.service_types.includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => toggleService(s.id)} data-testid={`refer-service-${s.id}`}
                  className={`text-left p-5 border-2 transition ${active ? "border-njas-sage bg-[var(--njas-sage-tint)]" : "border-[var(--njas-line-strong)] hover:border-njas-sage/60"}`}>
                  <div className="njas-condensed tracking-[0.14em] font-semibold text-sm text-njas-navy">{s.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Justice + urgency */}
        <div className="pt-10 border-t border-[var(--njas-sage)]/50 grid sm:grid-cols-2 gap-8">
          <Field label="Justice involvement">
            <select value={f.justice_involvement} onChange={upd("justice_involvement")} className={selectCls} data-testid="refer-justice">
              {JUSTICE.map((j) => <option key={j.v} value={j.v}>{j.l}</option>)}
            </select>
          </Field>
          <Field label="Urgency" help="If it's a crisis, please also call Lifeline 13 11 14.">
            <select value={f.urgency} onChange={upd("urgency")} className={selectCls} data-testid="refer-urgency">
              <option value="low">Low</option>
              <option value="standard">Standard</option>
              <option value="high">High</option>
              <option value="crisis">Crisis</option>
            </select>
          </Field>
        </div>

        {/* Reason */}
        <div className="pt-10 border-t border-[var(--njas-sage)]/50 space-y-8">
          <Field label="Reason for referral" required help="A brief summary is fine. We'll follow up for detail.">
            <textarea required rows={4} value={f.reason} onChange={upd("reason")} className={inputCls} data-testid="refer-reason" />
          </Field>
          <Field label="Additional info (optional)">
            <textarea rows={3} value={f.additional_info} onChange={upd("additional_info")} className={inputCls} data-testid="refer-additional" />
          </Field>
        </div>

        {/* Consent + submit */}
        <div className="pt-10 border-t border-[var(--njas-sage)]/50 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
          <label className="flex items-start gap-3 text-sm max-w-xl text-njas-text">
            <input required type="checkbox" checked={f.consent_given} onChange={upd("consent_given")} className="mt-1 accent-njas-navy" data-testid="refer-consent" />
            I consent to NJAS storing this information for the purpose of responding to this referral, in accordance with the Privacy &amp; Confidentiality Policy.
            <span className="inline-flex items-center gap-1 text-njas-sage-deep text-xs shrink-0"><ShieldCheck className="w-3 h-3" /> confidential</span>
          </label>
          <button type="submit" disabled={busy} data-testid="refer-submit" className="njas-condensed tracking-[0.22em] font-semibold text-[13px] inline-flex items-center gap-3 bg-njas-navy text-njas-bg px-8 py-4 hover:bg-njas-navy-hover disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit Referral
          </button>
        </div>
      </form>
    </div>
  );
}
